// P1-004: Hardware Zustand store — slot & inventory management
import { create } from 'zustand';
import type { HardwareItem, HardwareSlot } from '../types/hardware-internal';
import type { HardwareSpecialEffect } from '@/shared/types/common';
import { HardwareType } from '@/shared/types/common';
import { createInitialHardware } from '../data/hardware-configs';

// ─────────────────────────────────────────────────────
// State interface
// ─────────────────────────────────────────────────────

interface HardwareState {
  slots: HardwareSlot[];
  inventory: HardwareItem[];

  // Mutations
  installHardware: (slotIndex: number, item: HardwareItem) => boolean;
  uninstallHardware: (slotIndex: number) => HardwareItem | null;
  addToInventory: (item: HardwareItem) => void;
  removeFromInventory: (instanceId: string) => HardwareItem | null;
  sellHardware: (instanceId: string) => number;

  // Getters
  getTotalCompute: () => number;
  getTotalPower: () => number;
  getTotalPowerLimit: () => number;
  getTotalCooling: () => number;
  getTotalHeatGen: () => number;
  getOverloadValue: () => number;
  getSpecialEffects: () => HardwareSpecialEffect[];
  getInstalledItems: () => HardwareItem[];
}

// ─────────────────────────────────────────────────────
// Initial state: FTUE hardware installed in first 6 slots
// ─────────────────────────────────────────────────────

function createInitialState(): { slots: HardwareSlot[]; inventory: HardwareItem[] } {
  const initialItems = createInitialHardware();
  const slots: HardwareSlot[] = [];

  // Create 6 initial slots
  for (let i = 0; i < 6; i++) {
    slots.push({
      slotIndex: i,
      installed: i < initialItems.length ? (initialItems[i] ?? null) : null,
    });
  }

  return { slots, inventory: [] };
}

// ─────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────

export const useHardwareStore = create<HardwareState>()((set, get) => {
  const initial = createInitialState();

  return {
    slots: initial.slots,
    inventory: initial.inventory,

    installHardware: (slotIndex, item) => {
      const state = get();
      const slot = state.slots[slotIndex];
      if (!slot || slot.installed !== null) return false;

      // Remove from inventory
      const inventoryItem = state.inventory.find((i) => i.instanceId === item.instanceId);
      if (!inventoryItem) return false;

      set({
        slots: state.slots.map((s) =>
          s.slotIndex === slotIndex ? { ...s, installed: item } : s
        ),
        inventory: state.inventory.filter((i) => i.instanceId !== item.instanceId),
      });
      return true;
    },

    uninstallHardware: (slotIndex) => {
      const state = get();
      const slot = state.slots[slotIndex];
      if (!slot || slot.installed === null) return null;

      const item = slot.installed;
      set({
        slots: state.slots.map((s) =>
          s.slotIndex === slotIndex ? { ...s, installed: null } : s
        ),
        inventory: [...state.inventory, item],
      });
      return item;
    },

    addToInventory: (item) => {
      set((state) => ({ inventory: [...state.inventory, item] }));
    },

    removeFromInventory: (instanceId) => {
      const state = get();
      const item = state.inventory.find((i) => i.instanceId === instanceId);
      if (!item) return null;
      set({ inventory: state.inventory.filter((i) => i.instanceId !== instanceId) });
      return item;
    },

    sellHardware: (instanceId) => {
      const state = get();
      const item = state.inventory.find((i) => i.instanceId === instanceId);
      if (!item) return 0;
      const resellPrice = Math.floor(item.price * 0.5);
      set({ inventory: state.inventory.filter((i) => i.instanceId !== instanceId) });
      return resellPrice;
    },

    // ─── Getters ──────────────────────────────────

    getTotalCompute: () => {
      return get()
        .slots.reduce((sum, s) => sum + (s.installed?.compute ?? 0), 0);
    },

    getTotalPower: () => {
      // Power usage = sum of power for non-PSU items
      return get()
        .slots.reduce(
          (sum, s) =>
            sum + (s.installed && !s.installed.isPSU ? s.installed.power : 0),
          0
        );
    },

    getTotalPowerLimit: () => {
      // Power limit = sum of power for PSU items
      return get()
        .slots.reduce(
          (sum, s) =>
            sum + (s.installed?.isPSU ? s.installed.power : 0),
          0
        );
    },

    getTotalCooling: () => {
      // Cooling provided = sum of cooling for COOLER type items
      return get()
        .slots.reduce(
          (sum, s) =>
            sum + (s.installed?.type === HardwareType.COOLER ? s.installed.cooling : 0),
          0
        );
    },

    getTotalHeatGen: () => {
      // Heat generated = sum of cooling (as heat demand) for non-COOLER items
      return get()
        .slots.reduce(
          (sum, s) =>
            sum + (s.installed && s.installed.type !== HardwareType.COOLER ? s.installed.cooling : 0),
          0
        );
    },

    getOverloadValue: () => {
      const state = get();
      const powerUsage = state.getTotalPower();
      const powerLimit = state.getTotalPowerLimit();
      const heatGen = state.getTotalHeatGen();
      const cooling = state.getTotalCooling();
      return Math.max(0, powerUsage - powerLimit) + Math.max(0, heatGen - cooling);
    },

    getSpecialEffects: () => {
      return get()
        .slots.reduce<HardwareSpecialEffect[]>((effects, s) => {
          if (s.installed?.specialEffect) {
            effects.push(s.installed.specialEffect);
          }
          return effects;
        }, []);
    },

    getInstalledItems: () => {
      return get()
        .slots.filter((s): s is HardwareSlot & { installed: HardwareItem } => s.installed !== null)
        .map((s) => s.installed);
    },
  };
});
